import { Injectable, Signal, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * localStorage key holding the slug of the partner whose panel was opened last, so that the
 * bare root URL (`/`) can send the user back to their own partner address instead of a
 * generic landing page. It is written only after that partner authorized the API key.
 *
 * The credentials themselves are NOT stored here: the authorized session (slug + key +
 * partner) is a single entry owned by `AuthService`, see its `SESSION_STORAGE_KEY`.
 */
export const PARTNER_SLUG_STORAGE_KEY = 'marketplace_admin_partner_slug';

/**
 * Slug used when nothing is remembered yet — the partner of the original single-partner
 * deployment, whose admins keep opening the panel without a slug in the URL.
 */
export const DEFAULT_PARTNER_SLUG = 'marketplace';

/** Lowercase alphanumeric groups joined by single hyphens, e.g. `marketplace`, `acme-fitness`. */
export const PARTNER_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const PARTNER_SLUG_MIN_LENGTH = 2;
export const PARTNER_SLUG_MAX_LENGTH = 64;

/**
 * Path the marketplace API is mounted at. It is the same for every partner: the backend is
 * ONE multi-partner service that resolves the partner from the API key, so the slug never
 * appears in an API URL — only in the panel's own address.
 */
export const MARKETPLACE_API_PATH = '/api/v2/marketplace';

/**
 * Normalizes a slug before it is validated or stored: partner slugs are case-insensitive,
 * so we keep a single canonical (lowercase) form.
 */
export function normalizePartnerSlug(slug: string): string {
  return (slug ?? '').trim().toLowerCase();
}

/** Checks that a slug can be used as the partner segment of the panel URL. */
export function isValidPartnerSlug(slug: string): boolean {
  const normalized = normalizePartnerSlug(slug);

  return (
    normalized.length >= PARTNER_SLUG_MIN_LENGTH &&
    normalized.length <= PARTNER_SLUG_MAX_LENGTH &&
    PARTNER_SLUG_PATTERN.test(normalized)
  );
}

/**
 * Owns the partner the admin panel is currently pointed at.
 *
 * The partner comes from the **panel URL**: every route lives under a partner segment
 * (`/acme/reports`, `/acme/login`), and the router guard feeds that segment in through
 * {@link applySlug}. One deployment therefore serves every partner, and every partner gets
 * its own bookmarkable address.
 *
 * The slug is NOT part of any API URL — the backend is a single multi-partner service and
 * identifies the partner by the API key. Inside the panel the slug's job is to keep one
 * partner's session from being used while looking at another partner's address.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerContextService {
  /** Seeded with the remembered partner so the very first navigation already has a slug. */
  private readonly slugState = signal<string>(this.readRememberedSlug());

  /** Reactive view of the current slug, for templates and OnPush components. */
  readonly slugSignal: Signal<string> = this.slugState.asReadonly();

  /** Slug of the partner whose address the panel is currently on. */
  get slug(): string {
    return this.slugState();
  }

  /** Base URL of the marketplace API — the same for every partner. */
  get apiBaseUrl(): string {
    return `${environment.apiHost}${MARKETPLACE_API_PATH}`;
  }

  /**
   * Partner to send a user to when they open the panel without a slug (`/`): the one they
   * signed in to last, or the default partner on a fresh browser. Read from storage on every
   * call, so another tab's login is picked up without a reload.
   */
  get rememberedSlug(): string {
    return this.readRememberedSlug();
  }

  /**
   * Points the panel at the partner named in the URL. Does NOT persist: the URL is the
   * source of truth, and merely visiting an address is not a decision to remember it.
   *
   * @throws Error when the slug does not match the expected format.
   */
  applySlug(slug: string): void {
    this.slugState.set(this.requireValidSlug(slug));
  }

  /**
   * Remembers the partner for the next visit to the bare root URL. Called after that
   * partner's backend authorized the API key, never before.
   *
   * @throws Error when the slug does not match the expected format.
   */
  rememberSlug(slug: string): void {
    const normalized = this.requireValidSlug(slug);

    localStorage.setItem(PARTNER_SLUG_STORAGE_KEY, normalized);
    this.slugState.set(normalized);
  }

  /** Normalizes a slug and refuses anything that must not be treated as a partner address. */
  private requireValidSlug(slug: string): string {
    const normalized = normalizePartnerSlug(slug);

    if (!isValidPartnerSlug(normalized)) {
      throw new Error(`Invalid partner slug: "${slug}"`);
    }

    return normalized;
  }

  /**
   * Reads the remembered slug. A stored value that no longer passes validation (manually
   * edited storage, format change) is ignored rather than turned into a redirect target.
   */
  private readRememberedSlug(): string {
    const stored = localStorage.getItem(PARTNER_SLUG_STORAGE_KEY);

    return stored && isValidPartnerSlug(stored) ? normalizePartnerSlug(stored) : DEFAULT_PARTNER_SLUG;
  }
}
